import { useCallback, useEffect, useState } from 'react'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  CheckCircle2,
  CheckSquare,
  Circle,
  Clock,
  Loader2,
  Plus,
  Trash2,
  User as UserIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import TaskFormModal from '../tasks/TaskFormModal'
import {
  type TaskDto,
  type TaskListResponse,
  type TaskPriorityT,
} from '@toga/shared'

const PRIORITY_STYLES: Record<TaskPriorityT, string> = {
  BAJA: 'border-slate-500/30 text-slate-600 dark:text-slate-300 bg-slate-500/10',
  MEDIA: 'border-blue-500/40 text-blue-700 dark:text-blue-300 bg-blue-500/10',
  ALTA: 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10',
  URGENTE: 'border-rose-500/40 text-rose-700 dark:text-rose-200 bg-rose-500/15',
}

interface Props {
  opportunityId: string
  canWrite: boolean
}

export default function CaseTasksPanel({ opportunityId, canWrite }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [items, setItems] = useState<TaskDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<TaskDto | 'new' | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<TaskListResponse>(`/tasks?opportunityId=${opportunityId}`)
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => {
    void reload()
  }, [reload])

  const onToggle = async (task: TaskDto) => {
    try {
      if (task.status === 'COMPLETADA') {
        await api.patch(`/tasks/${task.id}`, { status: 'PENDIENTE' })
      } else {
        await api.post(`/tasks/${task.id}/complete`, {})
      }
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  const onDelete = async (task: TaskDto) => {
    if (
      !(await confirmDialog({
        message: t('tasks.confirmDelete', { title: task.title }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/tasks/${task.id}`)
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? 'Error')
    }
  }

  const pending = items.filter((i) => i.status !== 'COMPLETADA' && i.status !== 'CANCELADA')
  const completed = items.filter((i) => i.status === 'COMPLETADA' || i.status === 'CANCELADA')

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <CheckSquare size={14} className="text-toga-700 dark:text-toga-300" />
          {t('tasks.title')}
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">
              ({pending.length} {pending.length !== 1 ? t('tasks.pendingPlural') : t('tasks.pendingSingular')})
            </span>
          )}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-700 dark:text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
          >
            <Plus size={12} /> {t('tasks.new')}
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1 mb-2">
          <AlertCircle size={11} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> {t('common:status.loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">{t('tasks.empty')}</p>
      ) : (
        <>
          {pending.length > 0 && (
            <ul className="divide-y divide-base/40 text-sm">
              {pending.map((task) => (
                <TaskRow key={task.id} task={task} canWrite={canWrite} onToggle={onToggle} onDelete={onDelete} onEdit={() => setEditing(task)} />
              ))}
            </ul>
          )}
          {completed.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs t-faint cursor-pointer hover:t-secondary">
                {t('tasks.completedCancelled', { count: completed.length })}
              </summary>
              <ul className="divide-y divide-base/40 text-sm mt-2">
                {completed.map((task) => (
                  <TaskRow key={task.id} task={task} canWrite={canWrite} onToggle={onToggle} onDelete={onDelete} onEdit={() => setEditing(task)} />
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {editing && (
        <TaskFormModal
          initial={editing === 'new' ? null : editing}
          defaultCaseId={opportunityId}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function TaskRow({
  task,
  canWrite,
  onToggle,
  onDelete,
  onEdit,
}: {
  task: TaskDto
  canWrite: boolean
  onToggle: (t: TaskDto) => void
  onDelete: (t: TaskDto) => void
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const done = task.status === 'COMPLETADA' || task.status === 'CANCELADA'
  return (
    <li className="py-2 flex items-start gap-2">
      {canWrite ? (
        <button
          type="button"
          onClick={() => onToggle(task)}
          className="mt-0.5 t-muted hover:text-toga-700 dark:hover:text-toga-200"
        >
          {done ? <CheckCircle2 size={14} className="text-emerald-700 dark:text-emerald-300" /> : <Circle size={14} />}
        </button>
      ) : (
        <span className="mt-0.5">
          {done ? <CheckCircle2 size={14} className="text-emerald-700 dark:text-emerald-300" /> : <Circle size={14} className="t-faint" />}
        </span>
      )}
      <button type="button" onClick={onEdit} className="flex-1 min-w-0 text-left">
        <div className={done ? 't-faint line-through text-sm' : 't-primary text-sm'}>
          {task.title}
        </div>
        <div className="text-[11px] t-muted flex items-center gap-2 flex-wrap mt-0.5">
          <span className={`px-1.5 py-0.5 rounded border text-[10px] ${PRIORITY_STYLES[task.priority]}`}>
            {t('enums:TaskPriority.' + task.priority)}
          </span>
          {task.assignedToName && (
            <span className="flex items-center gap-1">
              <UserIcon size={10} /> {task.assignedToName}
            </span>
          )}
          {task.dueAt && !done && (
            <DueBadge iso={task.dueAt} />
          )}
        </div>
      </button>
      {canWrite && (
        <button
          type="button"
          onClick={() => onDelete(task)}
          className="w-6 h-6 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
        >
          <Trash2 size={11} />
        </button>
      )}
    </li>
  )
}

function DueBadge({ iso }: { iso: string }) {
  const d = new Date(iso)
  const days = Math.ceil((d.getTime() - Date.now()) / (24 * 3600 * 1000))
  const cls = days < 0 ? 'text-rose-700 dark:text-rose-300' : days <= 3 ? 'text-amber-700 dark:text-amber-300' : 't-secondary'
  return (
    <span className={`inline-flex items-center gap-1 ${cls}`}>
      <Clock size={10} /> {d.toLocaleDateString(currentLocaleTag())}
    </span>
  )
}
